import moment from "moment";
import { LoaderFunctionArgs, redirect, useLoaderData } from "react-router-dom";
import { ShowRow } from "../../components/ShowRow";
import { ShowLayout } from "../../layout/ShowLayout";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { Order } from "../../types/order";
import { capitalize } from "../../utils";
import {GET_ORDER_TICKETING} from "./queries";
import { TotalList } from "./components/Total";
import { ItemsTicketingTable } from "./components/ItemsTable";
import { DiscountTable } from "./components/DiscountTable";
import {TransactionTicketingTable} from "./components/TransactionTable";
import ButtonGroup from "../../components/Button";
import {generateReceiptTicketing} from "./actions/generate-new-receipt";
import { useRef, useState } from "react";
import {RefundTicketingModal} from "./components/RefundTicketingModal";

const client = createApolloClient();

// Same rule as orderShowLoader (RP-2563): resolve by id, then send the browser
// to the canonical URL when the reference in the link is not the order's.
export const orderTicketingShowLoader = async ({ params }: LoaderFunctionArgs) => {
  const id = Number(params.id);
  if (!Number.isInteger(id)) {
    throw new Response("Order not found", { status: 404 });
  }

  const { data } = await client.query({
    query: GET_ORDER_TICKETING,
    variables: { id },
  });
  const order = data.orders_ticketing?.[0];
  if (!order) {
    throw new Response(`Order ${id} not found`, { status: 404 });
  }
  if (params.reference_id !== order.reference_id) {
    return redirect(`/orders/ticketing/${encodeURIComponent(order.reference_id)}/${order.id}`);
  }
  return order;
};

export const OrderTicketingShow = () => {
  const [generating, setGenerating] = useState(false);
  const [partial, setPartial] = useState(false);
  const refundButton = useRef<HTMLButtonElement>(null);
  const order = useLoaderData() as Order;

  return (
    <>
      <ShowLayout
        header={`Order #${order.id}`}
        actions={
          <div className="flex space-x-2">
            <ButtonGroup
              loading={generating}
              mainButton={{
                name: "Generate Receipt",
                onClick: async () => {
                  setGenerating(true);
                  await generateReceiptTicketing(order.reference_id);
                  setGenerating(false);
                },
              }}
              buttons={[
                {
                  name: "Void",
                  onClick: () => {
                    refundButton.current?.click();
                  },
                },
                {
                  name: "Refund",
                  onClick: () => {
                    setPartial(true);
                    refundButton.current?.click();
                  },
                },
              ]}
            />
          </div>
        }
      >
      <ShowRow
          label="Tablet Transaction Time"
          value={moment(order.transaction_at).format("lll")}
      />
        <ShowRow
          label="Server Transaction Time"
          value={moment(order.created_at).format("lll")}
        />
      <ShowRow
          label="Last Updated Time"
          value={moment(order.updated_at).format("lll")}
      />
        <ShowRow label="Phone Number" value={order.phone_number} />
        <ShowRow
          label="Order Status"
          value={
            order.status === "refund" ? "Refunded" : capitalize(order.status)
          }
        />
        <ShowRow label="Reference ID" value={order.reference_id} />
        <ShowRow label="MX Ref ID" value={order.mx_ref_id} />
          <ShowRow label="Event" value={order.event?.name} />
      </ShowLayout>
       <div className="mt-2">
        <ShowLayout header="Items">
          <ItemsTicketingTable items={order.items} />
        </ShowLayout>
      </div>
      {order.discount && (
        <div className="mt-2">
          <ShowLayout header="Discounts">
            <DiscountTable />
          </ShowLayout>
        </div>
      )}
      <div className="mt-2">
        <ShowLayout header="Transactions">
          <TransactionTicketingTable />
        </ShowLayout>
      </div>
      <div className="mt-2">
        <TotalList />
      </div>
     <RefundTicketingModal partial={partial} onClose={() => setPartial(false)}>
        {({ setOpen }) => (
          <button ref={refundButton} onClick={() => setOpen(true)} />
        )}
      </RefundTicketingModal>
    </>
  );
};
